import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
    AcademicCapIcon,
    MapPinIcon,
    CalendarIcon,
    UsersIcon,
    StarIcon,
    BuildingOfficeIcon,
    CurrencyRupeeIcon,
    BookOpenIcon,
    TrophyIcon,
    CameraIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    XMarkIcon,
    PlayIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PhoneIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { universityAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import EnquiryModal from '../../components/Common/EnquiryModal'

const UniversityDetail = () => {
    const { slug } = useParams()
    const [activeTab, setActiveTab] = useState('overview')
    const [enquiryModal, setEnquiryModal] = useState({
        isOpen: false,
        university: null,
        type: ''
    })
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [isGalleryOpen, setIsGalleryOpen] = useState(false)
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0)

    // Fetch university data
    const { data: universityData, isLoading, error } = useQuery({
        queryKey: ['university', slug],
        queryFn: () => universityAPI.getUniversity(slug),
        enabled: !!slug
    })

    const university = universityData?.data?.university

    // Fetch university courses
    const { data: universityCoursesData } = useQuery({
        queryKey: ['universityCourses', university?._id],
        queryFn: () => universityAPI.getUniversityCourses(university._id),
        enabled: !!university?._id
    })

    // Deduplicate courses to prevent duplicate rendering
    const universityCourses = useMemo(() => {
        const courses = universityCoursesData?.data?.courses || [];
        // Remove duplicates based on _id and slug
        const seen = new Set();
        const uniqueCourses = courses.filter((course) => {
            const identifier = `${course._id}-${course.slug}`;
            if (seen.has(identifier)) {
                return false;
            }
            seen.add(identifier);
            return true;
        });
        return uniqueCourses;
    }, [universityCoursesData])

    // Extract scholarships from courses
    const universityScholarships = universityCourses
        .filter(course => course.scholarships && course.scholarships.trim() !== '')
        .map(course => ({
            _id: course._id,
            slug: course.slug,
            name: `${course.degreeId ? course.degreeId.name : course.level} - Scholarships`,
            description: course.scholarships,
            courseName: course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`,
            courseLevel: course.level,
            courseDuration: course.duration,
            courseSpecialization: course.specialization
        }))

    // Define tabs array before using it in useEffect
    const tabs = [
        { id: 'overview', name: 'Overview', icon: BuildingOfficeIcon },
        { id: 'courses-fees', name: 'Courses & Fees', icon: BookOpenIcon },
        { id: 'admission', name: 'Admission', icon: AcademicCapIcon },
        { id: 'placements', name: 'Placements', icon: TrophyIcon },
        // { id: 'scholarships', name: 'Scholarship', icon: CurrencyRupeeIcon }, // Hidden as requested
        { id: 'gallery', name: 'Gallery', icon: CameraIcon }
    ]

    // Auto-slide banner effect
    useEffect(() => {
        if (university?.banner && university.banner.length > 1) {
            const interval = setInterval(() => {
                setCurrentBannerIndex((prevIndex) =>
                    (prevIndex + 1) % university.banner.length
                )
            }, 5000) // Change slide every 5 seconds

            return () => clearInterval(interval)
        }
    }, [university?.banner])

    // Scroll-based tab changes
    useEffect(() => {
        const handleScroll = () => {
            const sections = tabs.map(tab => document.getElementById(tab.id))
            const scrollPosition = window.scrollY + 100 // Reduced offset for better responsiveness
            const windowHeight = window.innerHeight

            // Find which section is currently in view
            for (let i = 0; i < sections.length; i++) {
                const section = sections[i]
                if (section) {
                    const sectionTop = section.offsetTop
                    const sectionBottom = sectionTop + section.offsetHeight

                    // Check if section is in viewport
                    if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                        setActiveTab(tabs[i].id)
                        break
                    }
                }
            }

            // Handle case when scrolled past all sections
            const lastSection = sections[sections.length - 1]
            if (lastSection && scrollPosition >= lastSection.offsetTop + lastSection.offsetHeight - windowHeight) {
                setActiveTab(tabs[tabs.length - 1].id)
            }
        }

        // Throttle scroll events for better performance
        let ticking = false
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll()
                    ticking = false
                })
                ticking = true
            }
        }

        window.addEventListener('scroll', throttledScroll, { passive: true })
        return () => window.removeEventListener('scroll', throttledScroll)
    }, [tabs])

    // All data is now available in the main university object

    const handleEnquiryClick = (type = 'enquiry') => {
        setEnquiryModal({
            isOpen: true,
            university: university,
            type: type
        })
    }

    const handleCloseEnquiryModal = () => {
        setEnquiryModal({
            isOpen: false,
            university: null,
            type: ''
        })
    }

    const handleTabClick = (tabId) => {
        setActiveTab(tabId)
        const element = document.getElementById(tabId)
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            })
        }
    }

    const handleGalleryImageClick = (index) => {
        setCurrentImageIndex(index)
        setIsGalleryOpen(true)
    }

    const nextImage = () => {
        const images = galleryData?.data?.gallery || []
        setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }

    const prevImage = () => {
        const images = galleryData?.data?.gallery || []
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    if (error || !university) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="University not found" />
            </div>
        )
    }

    const renderOverviewTab = () => {
        const hasDescription = university?.introduction
        const hasStats = false // Simplified model doesn't have these stats
        const hasFacilities = false // Simplified model doesn't have facilities
        const hasInfrastructure = false // Simplified model doesn't have infrastructure

        if (!hasDescription && !hasStats && !hasFacilities && !hasInfrastructure) {
            return (
                <div id="overview" className="bg-white rounded-xl shadow-soft p-8 text-center">
                    <BuildingOfficeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Overview Not Available</h3>
                    <p className="text-gray-600">Overview details are not available for this university.</p>
                </div>
            )
        }

        return (
            <div id="overview" className="space-y-8 py-8">
                {/* University Description */}
                {hasDescription && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">About {university.name}</h3>
                        <div className="prose prose-lg max-w-none">
                            <div
                                className="text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: university.introduction }}
                            />
                        </div>
                    </div>
                )}

                {/* Key Information */}
                {hasStats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {overview?.established_year && (
                            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow-soft p-6 text-center border border-primary-200">
                                <CalendarIcon className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                                <h4 className="text-lg font-semibold text-gray-900">Established</h4>
                                <p className="text-2xl font-bold text-primary-600">{overview.established_year}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Facilities */}
                {hasFacilities && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Facilities</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {overview.facilities.map((facility, index) => (
                                <div key={index} className="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                                    <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{facility.name}</h4>
                                        {facility.description && (
                                            <p className="text-sm text-gray-600">{facility.description}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Infrastructure */}
                {hasInfrastructure && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Infrastructure</h3>
                        <div className="prose prose-lg max-w-none">
                            <p className="text-gray-700 leading-relaxed">{overview.infrastructure}</p>
                        </div>
                    </div>
                )}

                {/* Approvals & Recognition */}
                {university.approvals && university.approvals.length > 0 && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Approvals & Recognition</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {university.approvals.map((approval, index) => (
                                <div key={index} className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg border border-green-200">
                                    <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{approval.name || approval}</h4>
                                        {approval.category && (
                                            <p className="text-sm text-gray-600">{approval.category}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }


    const renderAdmissionTab = () => {
        return (
            <div id="admission" className="space-y-8 py-8">
                {/* Admission Information */}
                {university?.admission && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Admission Information</h3>
                        <div className="prose prose-lg max-w-none">
                            <div
                                className="text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: university.admission }}
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderPlacementsTab = () => {
        return (
            <div id="placements" className="space-y-8 py-8">
                {/* Placement Information */}
                {university?.placements && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Placement Information</h3>
                        <div className="prose prose-lg max-w-none">
                            <div
                                className="text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: university.placements }}
                            />
                        </div>
                    </div>
                )}

                {/* Top Recruiters */}
                {university?.top_recruiters && university.top_recruiters.length > 0 && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Top Recruiters</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {university.top_recruiters.map((recruiter, index) => (
                                <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-semibold text-gray-900">{recruiter.company_name}</h4>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderScholarshipsTab = () => {
        if (!universityScholarships || universityScholarships.length === 0) {
            return (
                <div id="scholarships" className="space-y-8 py-8">
                    <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                        <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Scholarships Information</h3>
                        <p className="text-gray-600">
                            No scholarship information is available for courses at this university at the moment.
                        </p>
                    </div>
                </div>
            )
        }

        return (
            <div id="scholarships" className="space-y-8 py-8">
                <div className="bg-white rounded-xl shadow-soft p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Course Scholarships</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {universityScholarships.map((scholarship) => (
                            <div key={scholarship._id} className="bg-gradient-to-br from-yellow-50 to-orange-100 rounded-xl shadow-soft p-6 border border-yellow-200 hover:shadow-lg transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            {scholarship.name}
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            <strong>Course:</strong> {scholarship.courseName}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {scholarship.courseLevel && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {scholarship.courseLevel}
                                                </span>
                                            )}
                                            {scholarship.courseDuration && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    {scholarship.courseDuration}
                                                </span>
                                            )}
                                        </div>
                                        {scholarship.description && (
                                            <div className="prose prose-sm max-w-none mb-4">
                                                <div dangerouslySetInnerHTML={{ __html: scholarship.description }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <Link
                                        to={`/courses/${scholarship.slug}`}
                                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                                    >
                                        View Course Details →
                                    </Link>
                                    <button
                                        onClick={() => handleEnquiryClick('enquiry')}
                                        className="bg-primary-600 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                                    >
                                        Enquire About Scholarships
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* General Scholarship Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Scholarship Information</h3>
                    <div className="prose max-w-none">
                        <p className="text-gray-700 mb-4">
                            This university offers various scholarship programs through its courses to support deserving students.
                            Scholarships are awarded based on academic merit, financial need, and other criteria specific to each course.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => handleEnquiryClick('counselling')}
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Get Scholarship Guidance
                            </button>
                            <button
                                onClick={() => handleEnquiryClick('brochure')}
                                className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                            >
                                Download Brochure
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderGalleryTab = () => {
        return (
            <div id="gallery" className="space-y-8 py-8">
                {university?.gallery && university.gallery.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {university.gallery.map((image, index) => (
                            <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden">
                                <img
                                    src={image.url}
                                    alt={image.caption || `Gallery image ${index + 1}`}
                                    className="w-full h-48 object-cover"
                                />
                                {image.caption && (
                                    <div className="p-4">
                                        <p className="text-sm text-gray-600">{image.caption}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                        <CameraIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Gallery Images</h3>
                        <p className="text-gray-600">Gallery images are not available for this university at the moment.</p>
                    </div>
                )}
            </div>
        )
    }

    const renderReviewsTab = () => {
        return (
            <div className="space-y-8">
                <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                    <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Student Reviews</h3>
                    <p className="text-gray-600">
                        Student reviews are not available for this university at the moment.
                    </p>
                </div>
            </div>
        )
    }

    const renderCoursesFeesTab = () => {
        if (!universityCourses || universityCourses.length === 0) {
            return (
                <div id="courses-fees" className="space-y-8 py-8">
                    <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                        <BookOpenIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Courses & Fees</h3>
                        <p className="text-gray-600">
                            Course information is not available for this university at the moment.
                        </p>
                    </div>
                </div>
            )
        }

        return (
            <div id="courses-fees" className="space-y-8 py-8">
                <div className="bg-white rounded-xl shadow-soft p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Available Courses</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {universityCourses.map((course) => (
                            <div key={course._id} className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow-soft p-6 border border-primary-200 hover:shadow-lg transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            {course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`}
                                        </h4>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {course.level && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {course.level}
                                                </span>
                                            )}
                                            {course.duration && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    {course.duration}
                                                </span>
                                            )}
                                        </div>
                                        {course.specialization && course.specialization.length > 0 && (
                                            <p className="text-sm text-gray-600 mb-3">
                                                <strong>Specialization:</strong> {course.specialization.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <Link
                                        to={`/courses/${course.slug}`}
                                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                                    >
                                        View Details →
                                    </Link>
                                    <button
                                        onClick={() => handleEnquiryClick('enquiry')}
                                        className="bg-primary-600 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                                    >
                                        Enquire
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Fee Range Information */}
                {university?.fee_range && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Fee Range</h3>
                        <div className="flex items-center space-x-3">
                            <CurrencyRupeeIcon className="w-8 h-8 text-green-600" />
                            <div>
                                <h4 className="text-xl font-semibold text-green-700">
                                    {university.fee_range.type === 'below_2_lakhs' ? 'Below ₹2 Lakhs' : 'Above ₹2 Lakhs'}
                                </h4>
                                <p className="text-gray-600">General fee range for courses at this university</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }



    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return renderOverviewTab()
            case 'courses-fees':
                return renderCoursesFeesTab()
            case 'admission':
                return renderAdmissionTab()
            case 'placements':
                return renderPlacementsTab()
            case 'scholarships':
                return renderScholarshipsTab()
            case 'gallery':
                return renderGalleryTab()
            default:
                return renderOverviewTab()
        }
    }

    return (
        <>
            <Helmet>
                <title>{university.name} - University Details | Margdarshak</title>
                <meta name="description" content={university.introduction ? university.introduction.replace(/<[^>]*>/g, '').substring(0, 160) : 'University details'} />
                <meta name="keywords" content={`${university.name}, university, education`} />
            </Helmet>

            {/* Redesigned Header Section */}
            <div className="bg-white relative overflow-hidden pt-20 lg:pt-28 pb-12 lg:pb-16">
                {/* Subtle Grid Pattern Background */}
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Left Column: Information */}
                        <div className="lg:col-span-7 space-y-6">
                            {/* Featured Badge (if applicable) */}
                            {university.featured && (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="inline-block bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2 border border-accent-200"
                                >
                                    Top University
                                </motion.div>
                            )}

                            {/* Title & Logo */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="space-y-4"
                            >
                                <div className="flex items-center space-x-4">
                                    {university.logo?.url && (
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-2xl shadow-sm border border-gray-100 p-2 flex-shrink-0 flex items-center justify-center">
                                            <img src={university.logo.url} alt="Logo" className="w-full h-full object-contain" />
                                        </div>
                                    )}
                                    <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight">
                                        <span className="text-accent-600">{university.name.split(' ')[0]}</span> {university.name.split(' ').slice(1).join(' ')}
                                    </h1>
                                    <button className="p-2 hover:bg-gray-100 rounded-full transition-colors hidden sm:block">
                                        <CheckCircleIcon className="w-6 h-6 text-primary-600" />
                                    </button>
                                </div>

                                {/* Highlights List */}
                                {university.approvals && university.approvals.length > 0 && (
                                    <div className="space-y-2">
                                        {university.approvals.slice(0, 3).map((approval, idx) => (
                                            <div key={idx} className="flex items-center text-gray-700 font-semibold">
                                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                                                <span>{approval.name} Approved</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>

                            {/* Popular Courses */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="pt-4"
                            >
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider block mb-4">Popular Course Offered :</span>
                                <div className="flex flex-wrap gap-2">
                                    {universityCourses.slice(0, 3).map((course, idx) => (
                                        <span key={idx} className="bg-primary-50 text-primary-700 px-4 py-1.5 rounded-full text-xs font-bold border border-primary-100 italic">
                                            {course.degreeId?.shortName || course.degreeId?.name || 'Top Program'}
                                        </span>
                                    ))}
                                    <span className="bg-gray-100 text-gray-600 px-4 py-1.5 rounded-full text-xs font-bold border border-gray-200">
                                        +Many More
                                    </span>
                                </div>
                            </motion.div>

                            {/* Approval Logos Row */}
                            {university.approvals && university.approvals.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="flex flex-wrap items-center gap-4 pt-6"
                                >
                                    {university.approvals.slice(0, 5).map((approval, idx) => (
                                        <div key={idx} className="w-12 h-12 bg-white rounded-lg border border-gray-100 shadow-sm flex items-center justify-center p-2 hover:shadow-md transition-shadow grayscale hover:grayscale-0">
                                            <span className="text-[10px] font-black text-primary-600 text-center uppercase leading-none">{approval.name?.substring(0, 4) || 'AUTH'}</span>
                                        </div>
                                    ))}
                                    <div className="w-10 h-10 rounded-full border-2 border-primary-100 flex items-center justify-center text-primary-600 hover:bg-primary-600 hover:text-white transition-all cursor-pointer">
                                        <CheckCircleIcon className="w-5 h-5" />
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Right Column: Visual Media & CTA */}
                        <div className="lg:col-span-5 space-y-6">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="relative rounded-3xl overflow-hidden shadow-2xl group border-4 border-white"
                            >
                                {university.banner && university.banner.length > 0 ? (
                                    <img
                                        src={university.banner[0].url}
                                        alt={university.name}
                                        className="w-full h-[300px] lg:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                ) : (
                                    <div className="w-full h-[300px] lg:h-[400px] bg-gradient-to-br from-primary-600 to-primary-900 flex items-center justify-center">
                                        <BuildingOfficeIcon className="w-20 h-20 text-white/20" />
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                                {/* The overlay logo has been moved to the left column */}
                            </motion.div>

                            {/* Quick Action Buttons */}
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => handleEnquiryClick('consultation')}
                                    className="bg-accent-600 text-white py-4 rounded-xl font-bold hover:bg-accent-700 transition-all active:scale-95 shadow-lg flex items-center justify-center space-x-2"
                                >
                                    <span>Let's Talk</span>
                                </button>
                                <button
                                    onClick={() => handleEnquiryClick('brochure')}
                                    className="bg-white text-primary-600 border-2 border-primary-600 py-4 rounded-xl font-bold hover:bg-primary-50 transition-all active:scale-95 shadow-lg flex items-center justify-center space-x-2"
                                >
                                    <span>Download Prospectus</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* General Info Card below Header */}
                    <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-8 bg-white border border-gray-100 rounded-3xl shadow-xl overflow-hidden flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            <div className="p-8 flex-1 space-y-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-black text-gray-900">General info</h3>
                                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-green-200">
                                        Admission closing soon !
                                    </span>
                                </div>
                                <div className="mt-4 prose prose-sm max-w-none text-gray-600 line-clamp-3">
                                    {university.introduction ? (
                                        <div dangerouslySetInnerHTML={{ __html: university.introduction }} />
                                    ) : (
                                        <p>Learn more about {university.name}, a distinguished institution offering quality education.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Fee Highlight Card */}
                        <div className="lg:col-span-4 bg-white border border-gray-100 rounded-3xl shadow-xl p-8 flex flex-col justify-center items-center text-center space-y-4">
                            <h3 className="text-xl font-bold text-primary-700">
                                Average fee of <br /> {university.name}
                            </h3>
                            <div className="space-y-1">
                                <div className="text-2xl lg:text-3xl font-black text-accent-600 flex items-center justify-center">
                                    <CurrencyRupeeIcon className="w-6 h-6 lg:w-8 lg:h-8 mr-1" />
                                    <span>{university.fee_range?.type === 'below_2_lakhs' ? 'Below 2 Lakhs' : university.fee_range?.type === 'above_2_lakhs' ? 'Above 2 Lakhs' : university.fee_range?.type || 'Contact for Fees'}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => handleEnquiryClick('counselling')}
                                className="w-full bg-primary-600 text-white py-4 rounded-2xl font-bold hover:bg-primary-700 transition-all shadow-lg mt-2"
                            >
                                Free counselling from expert
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
                {/* Tabs */}
                < div className="mb-8" >
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto">
                            {tabs.map((tab) => {
                                const Icon = tab.icon
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabClick(tab.id)}
                                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                                            ? 'border-primary-500 text-primary-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <Icon className="w-5 h-5" />
                                        <span>{tab.name}</span>
                                    </button>
                                )
                            })}
                        </nav>
                    </div>
                </div >

                {/* Tab Content */}
                < AnimatePresence mode="wait" >
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {renderTabContent()}
                    </motion.div>
                </AnimatePresence >


                {/* CTA Section */}
                < div className="mt-12 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-8 text-center text-white" >
                    <h3 className="text-2xl font-bold mb-4 text-white">Interested in {university.name}?</h3>
                    <p className="text-lg mb-6 text-white">Get personalized guidance and support for your admission process.</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => handleEnquiryClick('counselling')}
                            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                        >
                            Get Free Consultation
                        </button>
                        <a
                            href="tel:+919810530354"
                            className="bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                        >
                            <PhoneIcon className="w-5 h-5" />
                            <span>Call Us</span>
                        </a>
                        <button
                            onClick={() => handleEnquiryClick('enquiry')}
                            className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
                        >
                            Enquiry Now
                        </button>
                    </div>
                </div >
            </div >

            {/* Gallery Modal */}
            < AnimatePresence >
                {isGalleryOpen && (
                    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative max-w-4xl max-h-full"
                        >
                            <button
                                onClick={() => setIsGalleryOpen(false)}
                                className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                            <button
                                onClick={prevImage}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                            >
                                <ChevronLeftIcon className="w-6 h-6" />
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                            >
                                <ChevronRightIcon className="w-6 h-6" />
                            </button>
                            <img
                                src={(galleryData?.data?.gallery || [])[currentImageIndex]?.url || (galleryData?.data?.images || [])[currentImageIndex]}
                                alt={`Gallery image ${currentImageIndex + 1}`}
                                className="max-w-full max-h-full object-contain rounded-lg"
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence >



            {/* Floating Action Buttons */}
            <div className="fixed bottom-28 right-2 md:right-6 z-40 flex flex-col gap-2 md:gap-3">
                <button
                    onClick={() => handleEnquiryClick('enquiry')}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full shadow-lg font-semibold transition-all duration-200 transform hover:scale-105"
                >
                    Enquiry Now
                </button>
                <a
                    href="tel:+919810530354"
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-full shadow-lg font-semibold transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                    <PhoneIcon className="w-5 h-5" />
                    <span>Call Us</span>
                </a>
            </div>

            {/* Enquiry Modal */}
            < EnquiryModal
                isOpen={enquiryModal.isOpen}
                onClose={handleCloseEnquiryModal}
                title={
                    enquiryModal.type === 'enquiry' ? 'Quick Enquiry' :
                        enquiryModal.type === 'brochure' ? 'Download Brochure' :
                            enquiryModal.type === 'counselling' ? 'Free Career Counselling' :
                                'University Enquiry'
                }
                source={enquiryModal.type}
                itemName={enquiryModal.university?.name || ''}
                itemType="university"
                itemId={enquiryModal.university?._id || null}
            />
        </>
    )
}

export default UniversityDetail

